// apps/client/src/utils/quota.ts

import { OrganisationWithRole } from '@avanzu/types';

/**
 * Get the current financial year based on an organisation's tax year start month
 * @param taxYearStartMonth Month (1-12) when the financial year starts (default 7 = July)
 * @returns Current financial year string (e.g., "2023-24")
 */
export function getCurrentFinancialYear(taxYearStartMonth: number = 7): string {
  const now = new Date();
  const currentMonth = now.getMonth() + 1; // JavaScript months are 0-indexed
  const currentYear = now.getFullYear();

  // If we're before the tax year start month, we're still in the previous financial year
  const financialYearStart = currentMonth >= taxYearStartMonth ? currentYear : currentYear - 1;
  const financialYearEnd = financialYearStart + 1;

  return `${financialYearStart}-${String(financialYearEnd).slice(-2)}`;
}

/**
 * Check if an organisation can create a new project in a specific financial year
 * @param organisation The organisation with quota limits
 * @param financialYear The financial year to check (e.g., "2023-24")
 * @param projectCount Current number of projects in that financial year
 * @returns true if a new project can be created, false otherwise
 */
export function canCreateNewProject(
  organisation: OrganisationWithRole | null,
  financialYear: string,
  projectCount: number
): boolean {
  if (!organisation) return true; // No organisation context, allow by default

  const limit = organisation.quotaMaxProjectsPerYear;

  // -1 means unlimited
  if (limit === -1) return true;

  // Check if under the limit
  return projectCount < limit;
}

/**
 * Get quota information for project creation in a specific financial year
 * @param organisation The organisation with quota limits
 * @param financialYear The financial year to check (e.g., "2023-24")
 * @param projectCount Current number of projects in that financial year
 * @returns Quota information object
 */
export function getProjectQuotaInfo(
  organisation: OrganisationWithRole | null,
  financialYear: string,
  projectCount: number
): {
  limit: number;
  isUnlimited: boolean;
  current: number;
  remaining: number;
  isAtLimit: boolean;
  isNearLimit: boolean;
  canCreate: boolean;
} {
  if (!organisation) {
    return {
      limit: -1,
      isUnlimited: true,
      current: projectCount,
      remaining: Infinity,
      isAtLimit: false,
      isNearLimit: false,
      canCreate: true,
    };
  }

  const limit = organisation.quotaMaxProjectsPerYear;
  const isUnlimited = limit === -1;
  const remaining = isUnlimited ? Infinity : limit - projectCount;
  const isAtLimit = !isUnlimited && projectCount >= limit;
  const isNearLimit = !isUnlimited && remaining <= 1 && remaining > 0;
  const canCreate = canCreateNewProject(organisation, financialYear, projectCount);

  return {
    limit,
    isUnlimited,
    current: projectCount,
    remaining,
    isAtLimit,
    isNearLimit,
    canCreate,
  };
}

/**
 * Check if an organisation can create/import new transactions for a project
 * @param organisation The organisation with quota limits
 * @param transactionCount Current number of transactions in the project
 * @returns true if new transactions can be created, false otherwise
 */
export function canCreateNewTransactions(
  organisation: OrganisationWithRole | null,
  transactionCount: number
): boolean {
  if (!organisation) return true; // No organisation context, allow by default

  const limit = organisation.quotaMaxTransactionsPerProject;

  // -1 means unlimited
  if (limit === -1) return true;

  // Check if under the limit
  return transactionCount < limit;
}

/**
 * Get quota information for transaction creation/import for a project
 * @param organisation The organisation with quota limits
 * @param transactionCount Current number of transactions in the project
 * @returns Quota information object
 */
export function getTransactionQuotaInfo(
  organisation: OrganisationWithRole | null,
  transactionCount: number
): {
  limit: number;
  isUnlimited: boolean;
  current: number;
  remaining: number;
  isAtLimit: boolean;
  isNearLimit: boolean;
  canCreate: boolean;
} {
  if (!organisation) {
    return {
      limit: -1,
      isUnlimited: true,
      current: transactionCount,
      remaining: Infinity,
      isAtLimit: false,
      isNearLimit: false,
      canCreate: true,
    };
  }

  const limit = organisation.quotaMaxTransactionsPerProject;
  const isUnlimited = limit === -1;
  const remaining = isUnlimited ? Infinity : limit - transactionCount;
  const isAtLimit = !isUnlimited && transactionCount >= limit;
  const isNearLimit = !isUnlimited && remaining <= 100 && remaining > 0; // Warn at 100 remaining for transactions
  const canCreate = canCreateNewTransactions(organisation, transactionCount);

  return {
    limit,
    isUnlimited,
    current: transactionCount,
    remaining,
    isAtLimit,
    isNearLimit,
    canCreate,
  };
}

/**
 * Check if an organisation can add new users
 * @param organisation The organisation with quota limits
 * @param userCount Current number of users in the organisation
 * @returns true if new users can be added, false otherwise
 */
export function canAddNewUsers(
  organisation: OrganisationWithRole | null,
  userCount: number
): boolean {
  if (!organisation) return true; // No organisation context, allow by default

  const limit = organisation.quotaMaxUsers;

  // -1 means unlimited
  if (limit === -1) return true;

  // Check if under the limit
  return userCount < limit;
}

/**
 * Get quota information for user management
 * @param organisation The organisation with quota limits
 * @param userCount Current number of users in the organisation
 * @returns Quota information object
 */
export function getUserQuotaInfo(
  organisation: OrganisationWithRole | null,
  userCount: number
): {
  limit: number;
  isUnlimited: boolean;
  current: number;
  remaining: number;
  isAtLimit: boolean;
  isNearLimit: boolean;
  canAdd: boolean;
} {
  if (!organisation) {
    return {
      limit: -1,
      isUnlimited: true,
      current: userCount,
      remaining: Infinity,
      isAtLimit: false,
      isNearLimit: false,
      canAdd: true,
    };
  }

  const limit = organisation.quotaMaxUsers;
  const isUnlimited = limit === -1;
  const remaining = isUnlimited ? Infinity : limit - userCount;
  const isAtLimit = !isUnlimited && userCount >= limit;
  const isNearLimit = !isUnlimited && remaining <= 2 && remaining > 0; // Warn at 2 remaining for users
  const canAdd = canAddNewUsers(organisation, userCount);

  return {
    limit,
    isUnlimited,
    current: userCount,
    remaining,
    isAtLimit,
    isNearLimit,
    canAdd,
  };
}
